import { OAUTH_FAILED_PARAM, OAUTH_REFUSED_PARAM } from '../../common/EsConstants';
import { esLiteral } from '../../common/EsStrings';

/**
 * What to say when a browser comes back from the hosted UI without an account.
 *
 * ## Why this exists at all
 *
 * esback has sent a reason on the query string since the OAuth work landed --
 * `?oauthRefused=nameReserved` on the register page -- and **nothing on this
 * side has ever read it.** So a refused Google sign-in dropped somebody on the
 * register page with no explanation, which reads as a button that did nothing.
 * `?oauthFailed=` is the newer half, added the day a failed callback was found
 * to be landing on a 404 instead of anywhere at all.
 *
 * ## Two parameters, because they are different questions
 *
 * A REFUSAL is escapade's own decision about an identity that authenticated
 * perfectly well, and it lands on the register page because the way out is
 * usually to register differently. A FAILURE is the OAuth exchange not
 * completing, which is nothing the person did and nothing a different address
 * would fix, so it lands back on the login page where the button is. See
 * EsConstants.NM_OAUTH_REFUSED and NM_OAUTH_FAILED on the server, which is
 * where each set of values is defined.
 *
 * ## The wording lives HERE and not on the server
 *
 * The server sends a code -- `invalid_grant`, `emailUnverified` -- and this
 * file turns it into a sentence, because the sentence depends on what the page
 * around it already says and the server does not know that. It also keeps the
 * address out of the query string: a code names a protocol state, and a
 * sentence naming a person would end up in browser history and in the referer
 * of whatever the SPA fetches next.
 */

/**
 * A failed exchange, keyed on Spring's own OAuth2Error codes.
 *
 * Every sentence ends in "please try again" because for this whole family that
 * genuinely is the fix -- these are states that expire, not decisions. The two
 * named codes are the ones that actually happen: one from leaving the hosted UI
 * open or starting the flow twice, the other from a Back button onto an
 * authorization code that has already been spent.
 */
const failed = ( t ) => ( {
    authorization_request_not_found:
        t( 'account-oauth-failed-expired', 'That sign-in expired, or was started twice in the same browser. Please try again.' ),
    invalid_grant:
        t( 'account-oauth-failed-used', 'That sign-in link had already been used. Please try again.' ),
    invalid_state_parameter:
        t( 'account-oauth-failed-state', 'That sign-in did not come back the way it went out. Please try again.' ),
} );

const failedFallback = ( t ) => t( 'account-oauth-failed', 'Sorry — that sign-in could not be completed. Please try again.' );

/**
 * A refusal, keyed on EsOauthRegisterService.Outcome's own reasons.
 *
 * <b>None of these say "try again", and that is the difference from the map
 * above.</b> Every one is a settled fact about the identity that arrived, so a
 * sentence inviting a retry would be an invitation to press the same button and
 * get the same answer. Each says what would actually have to be different.
 */
const refused = ( t ) => ( {
    nameReserved:
        t( 'account-oauth-refused-reserved', 'That address is reserved for automated testing, so an account cannot be made for it.' ),
    noEmail:
        t( 'account-oauth-refused-no-email', 'Google did not share an email address, and an account here is an address. Try registering with one instead.' ),
    emailUnverified:
        t( 'account-oauth-refused-unverified', 'Google did not confirm that the address is yours, so it cannot open an account here. Try registering with one instead.' ),
    noIdentity:
        t( 'account-oauth-refused-no-identity', 'That sign-in did not say who you are. Try registering instead.' ),
    // esback's ENUM_LOGIN_DISABLED, onto the login page. Same sentence as XAccountDisabled.
    disabled:
        t( 'account-oauth-refused-disabled', 'This account can’t sign in. If you think that’s wrong, write to us with the contact form on the home page.' ),
} );

const refusedFallback = ( t ) => t( 'account-oauth-refused', 'Sorry — that sign-in could not be used to make an account here.' );

/**
 * The sentence for whichever of the two parameters is present, or null.
 *
 * <p>Null rather than an empty string, so a caller can put it straight into the
 * error state a page already has and get that page's existing "no error"
 * rendering for free.
 *
 * @param pParams the URLSearchParams from useSearchParams
 */
export function esOauthMessage( pParams, t = esLiteral ) {
    if ( !pParams ) return null;

    const aFailed = pParams.get( OAUTH_FAILED_PARAM );
    if ( aFailed ) return failed( t )[ aFailed ] ?? failedFallback( t );

    const aRefused = pParams.get( OAUTH_REFUSED_PARAM );
    if ( aRefused ) return refused( t )[ aRefused ] ?? refusedFallback( t );

    return null;
}
